import { useEffect, useState } from 'react';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useLiveTranslateMutation } from '@services/useLiveTranslateMutation';

import { selectAITranslateTargetLanguage } from '../store/useAITranslateStore';

interface ITranslateParams {
  item: any;
  fields: string[];
  entityName?: string;
  entityId?: string | number;
}

type TValue = { [key: string]: string };

const getTranslatesInitState = (item: any, fields: string[], targetLanguage: string) => {
  let hasTranslate = false;
  const initObj: TValue = {};
  const translatedObj: TValue = {};
  const fieldsWoTransArr: string[] = [];

  fields.forEach((field) => {
    const transField = item?.translates?.[targetLanguage]?.[field];

    if (!transField) {
      fieldsWoTransArr.push(field);
    }

    if (
      Object.keys(item?.translates).length &&
      (!transField || transField?.sourceLanguage !== targetLanguage)
    ) {
      hasTranslate = true;
    }

    initObj[field] = item?.[field] || '';
    translatedObj[field] = transField?.text || item?.[field] || '';
  });

  return { initObj, translatedObj, fieldsWoTransArr, hasTranslate, isTranslated: false };
};

const useLiveTranslate = ({ item, fields, entityName = 'action_list', entityId }: ITranslateParams) => {
  const { t } = useTranslation();
  const { mutate: liveTranslateMutation, isLoading: isInfoTranslateLoading } = useLiveTranslateMutation();

  const targetLanguage = selectAITranslateTargetLanguage();

  const [translates, setTranslates] = useState<{
    initObj: any;
    translatedObj: any;
    fieldsWoTransArr: string[];
    hasTranslate: boolean;
    isTranslated: boolean;
  }>({ initObj: {}, translatedObj: {}, fieldsWoTransArr: [], hasTranslate: false, isTranslated: false });

  useEffect(() => {
    if (item?.id) {
      setTranslates(getTranslatesInitState(item, fields, targetLanguage));
    }
  }, [item?.id]);

  const toggleIsTranslate = () => {
    if (translates.isTranslated) {
      return setTranslates({ ...translates, isTranslated: false });
    }
    if (!translates.isTranslated && !translates.fieldsWoTransArr.length) {
      return setTranslates({ ...translates, isTranslated: true });
    }

    const uniqKey = window.btoa(JSON.stringify(translates.fieldsWoTransArr) + targetLanguage);

    liveTranslateMutation(
      {
        fields: translates.fieldsWoTransArr,
        entity_name: entityName,
        entity_id: entityId,
        target_language: targetLanguage,
        source_language: '',
        key: uniqKey,
      },
      {
        onSuccess: ({ data }) => {
          if (!data.translation.length && !data.translation) {
            showToast({ variant: 'error', message: t('Translation error') });

            return;
          }
          const newTransObj = { ...translates.translatedObj };

          if (Object.keys(data?.translation).length) {
            translates.fieldsWoTransArr.forEach(
              (field) => (newTransObj[field] = data.translation[field]?.text || item[field]),
            );
          }

          setTranslates({
            ...translates,
            translatedObj: newTransObj,
            fieldsWoTransArr: [],
            isTranslated: true,
          });
        },
      },
    );
  };

  return {
    hasTranslate: translates.hasTranslate,
    translates: translates.isTranslated ? translates.translatedObj : translates.initObj,
    isTranslated: translates.isTranslated,
    isTranslateLoading: isInfoTranslateLoading,
    toggleIsTranslate,
  };
};

export default useLiveTranslate;
